import type { SerializedStyles } from '@emotion/react';

export type Segment = {
  type: 'plain' | 'highlight';
  content: string;
};

export type FormatPattern = {
  /** Must have exactly one capturing group for the content between delimiters */
  pattern: RegExp;
  type: string;
};

const splitByPattern = (text: string, pattern: RegExp, type: string): Segment[] => {
  const result: Segment[] = [];
  const re = new RegExp(pattern.source, pattern.flags);
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push({ type: 'plain', content: text.slice(lastIndex, match.index) });
    }

    result.push({ type: type as Segment['type'], content: match[1] ?? '' });
    lastIndex = re.lastIndex;
  }

  if (lastIndex < text.length) {
    result.push({ type: 'plain', content: text.slice(lastIndex) });
  }

  return result.length > 0 ? result : [{ type: 'plain', content: text }];
};

export const parseFormattedText = (text: string, patterns: FormatPattern[]): Segment[] => {
  if (patterns.length === 0) {
    return [{ type: 'plain', content: text }];
  }

  let segments: Segment[] = [{ type: 'plain', content: text }];

  for (const { pattern, type } of patterns) {
    const next: Segment[] = [];
    for (const seg of segments) {
      if (seg.type !== 'plain') {
        next.push(seg);
        continue;
      }
      const parts = splitByPattern(seg.content, pattern, type);
      next.push(...parts);
    }
    segments = next;
  }

  return segments;
};

export const toHTMLSegment = (
  segment: Segment,
  index: number,
  classes: Record<Segment['type'], SerializedStyles>,
): React.ReactNode => {
  switch (segment.type) {
    case 'plain':
      return <span key={index}>{segment.content}</span>;
    case 'highlight':
      return (
        <span key={index} css={classes.highlight}>
          {segment.content}
        </span>
      );
    default:
      return <span key={index}>{segment.content}</span>;
  }
};
